import React, { useEffect } from 'react';
import {
    Modal, Box, Typography, Paper, IconButton,
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, Divider, Tab, Tabs,
    CircularProgress
} from '@mui/material';
import Icon from '@mdi/react';
import {
    mdiCloseCircleOutline,
    mdiMapMarkerPath,
    mdiTruckDelivery,
    mdiRoadVariant,
    mdiClockOutline,
    mdiRuler,
    mdiEmailOutline,
    mdiPhoneOutline,
    mdiArrowRightBold,
    mdiCloudDownloadOutline,
    mdiDatabaseOutline,
    mdiHistory,
} from '@mdi/js';
import { blue, grey, green, orange } from '@mui/material/colors';
import { useDispatch } from 'react-redux';
import { getOneRoute } from '../../../features/tripcenter/routeSlice';

const RouteDetailView = ({ open, onClose, route }) => {
    const [activeTab, setActiveTab] = React.useState(0);
    const dispatch = useDispatch();

    // Effect to fetch the full route data if only partially loaded
    useEffect(() => {
        //console.log("RouteDetailView effect", routeid, selectedRouteId, route?.routeNumber, route?.routeName, route?.segments);
        if (open && route && route?.routeNumber !== undefined && route?.routeName !== undefined && route?.segments === undefined) {
            console.log("Dispatching getOneRoute", route.routeNumber, route.routeName);
            dispatch(getOneRoute({
                routeName: route.routeName,
                routeNumber: route.routeNumber
            }));

        }
    }, [open, route, dispatch]);
    useEffect(() => {
        //console.log('Rout e data fetched:', route?.routeNumber, route ?? 'No route data');
    }, [route]);
    // If no route is provided, don't render the modal
    if (!route) {
        return null;
    }

    // Check if the route is partially loaded (has routeName but no segments)
    const isPartiallyLoaded = !route.segments;

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Helper functions
    const formatTime = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
    };

    const formatRuleValue = (key, value) => {
        switch (key) {
            case 'drivingStartTime':
            case 'drivingEndTime':
                return value;
            case 'minFuelEfficiency':
                return `${value} km/L`;
            case 'maxFuelConsumption':
                return `${value} L`;
            case 'maxSpeed':
            case 'minSpeed':
                return `${value} km/h`;
            case 'maxDetentionTime':
                return `${value} minutes`;
            case String(key).toLowerCase().includes('percentage'):
                return `${value}%`;
            default:
                return value;
        }
    };

    const formatCategory = (category) => {
        switch (category) {
            case 'tripStage': return 'Trip Stage';
            case 'activeStatus': return 'Status Updates';
            case 'ruleViolation': return 'Rule Violations';
            case 'finalReport': return 'Final Report';
            default: return category;
        }
    };

    const formatDateOnly = (value) => {
        if (!value) return 'N/A';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return String(value);
        return date.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatTripPeriod = (entry) => {
        if (entry?.tripPeriod) {
            const [startRaw, endRaw] = entry.tripPeriod.split(' - ').map((part) => part.trim());
            const startParsed = formatDateOnly(startRaw);
            const endParsed = formatDateOnly(endRaw);
            if (startParsed === 'N/A' && endParsed === 'N/A') return entry.tripPeriod;
            if (startParsed === 'N/A') return `- ${endParsed}`;
            if (endParsed === 'N/A') return `${startParsed} -`;
            return `${startParsed} - ${endParsed}`;
        }
        const start = formatDateOnly(entry?.actualStartDate);
        const end = formatDateOnly(entry?.actualEndDate);
        if (start === 'N/A' && end === 'N/A') return 'N/A';
        return `${start} - ${end}`;
    };

    const revisionEntries = route?.archivedRoutes || [];

    const RouteLocationsView = ({ route }) => {
        const isRoundTrip = route?.routeType === 'roundTrip' || route?.routeType === 'roundTripWithStops';

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                {/* Route Type Indicator */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                        label={isRoundTrip ? "Round Trip Route" : "One Way Route"}
                        size="small"
                        sx={{
                            bgcolor: isRoundTrip ? green[100] : blue[100],
                            color: isRoundTrip ? green[800] : blue[800],
                            fontWeight: 500,
                            '& .MuiChip-label': { px: 2 }
                        }}
                    />
                </Box>

                {/* Horizontal Route Path Visualization - Expanded */}
                <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    p: 2,
                    bgcolor: 'white',
                    borderRadius: 2,
                    border: `1px solid ${grey[200]}`,
                    width: '100%'
                }}>
                    {/* Start Location */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: 'rgba(30, 144, 255, 0.05)',
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid rgba(30, 144, 255, 0.2)',
                        minWidth: '220px',
                        maxWidth: '340px',
                        flex: '1 1 auto'
                    }}>
                        <Box sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            mr: 1.5
                        }}>
                            <Box sx={{
                                width: '16px',
                                height: '16px',
                                bgcolor: 'DodgerBlue',
                                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)', // hexagon shape
                            }} />
                        </Box>
                        <Box>
                            <Typography variant="subtitle1" fontWeight={500} noWrap sx={{ maxWidth: '260px' }}>
                                {route?.startLocation?.locationName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Starting Point {route?.startLocation?.maxDetentionTime && `| ${route?.startLocation?.maxDetentionTime} Hr`}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Via Points if any */}
                    {route?.viaLocations?.length > 0 && route.viaLocations.map((point, index) => (
                        <Box key={index} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: 'rgba(218, 112, 214, 0.05)',
                            p: 1.5,
                            borderRadius: 2,
                            border: '1px solid rgba(218, 112, 214, 0.2)',
                            minWidth: '220px',
                            maxWidth: '340px',
                            flex: '1 1 auto'
                        }}>
                            <Icon
                                path={mdiArrowRightBold}
                                size={0.7}
                                color={grey[400]}
                                style={{ marginRight: '8px' }}
                            />
                            <Box sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'white',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                mr: 1.5
                            }}>
                                <Box sx={{
                                    width: '14px',
                                    height: '14px',
                                    bgcolor: 'Orchid',
                                    clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)', // square shape
                                }} />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight={500} noWrap sx={{ maxWidth: '220px' }}>
                                    {point.locationName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Stop {index + 1} {point.maxDetentionTime && `| ${point.maxDetentionTime} Hr`}
                                </Typography>
                            </Box>
                        </Box>
                    ))}

                    {/* End Location */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: 'rgba(0, 0, 128, 0.05)',
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid rgba(0, 0, 128, 0.2)',
                        minWidth: '220px',
                        maxWidth: '340px',
                        flex: '1 1 auto'
                    }}>
                        <Icon
                            path={mdiArrowRightBold}
                            size={0.7}
                            color={grey[400]}
                            style={{ marginRight: '8px' }}
                        />
                        <Box sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'white',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            mr: 1.5
                        }}>
                            <Box sx={{
                                width: '16px',
                                height: '16px',
                                bgcolor: 'Navy',
                                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)', // hexagon shape
                            }} />
                        </Box>
                        <Box>
                            <Typography variant="subtitle1" fontWeight={500} noWrap sx={{ maxWidth: '220px' }}>
                                {route?.endLocation?.locationName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {isRoundTrip ? 'Turnaround Point' : 'Final Destination'} {route?.endLocation?.maxDetentionTime && `| ${route?.endLocation?.maxDetentionTime} Hr`}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        );
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                '& .MuiBackdrop-root': {
                    backgroundColor: 'transparent'
                }
            }}
        >
            <Paper sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '90%',
                maxWidth: 1000,
                bgcolor: 'background.paper',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                {/* Header - Stays fixed */}
                <Box sx={{
                    bgcolor: 'primary.main',
                    p: 2,
                    color: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    flexShrink: 0
                }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Typography variant="h5" sx={{ fontWeight: 500 }}>
                            #Route Number: {route?.routeNumber}
                        </Typography>
                        <IconButton onClick={onClose} sx={{ color: 'white' }}>
                            <Icon path={mdiCloseCircleOutline} size={1} />
                        </IconButton>
                    </Box>
                    <Typography variant="subtitle1">
                        {route?.routeName || 'Unnamed Route'}
                    </Typography>
                </Box>

                {/* Scrollable content area */}
                <Box sx={{
                    overflowY: 'auto',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    {isPartiallyLoaded ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: 8,
                                height: '400px',
                                textAlign: 'center'
                            }}
                        >
                            <Box
                                sx={{
                                    mb: 4,
                                    width: 100,
                                    height: 100,
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <CircularProgress
                                    size={100}
                                    thickness={2}
                                    sx={{
                                        position: 'absolute',
                                        color: 'primary.light'
                                    }}
                                />
                                <Icon
                                    path={mdiDatabaseOutline}
                                    size={2}
                                    color={blue[700]}
                                />
                            </Box>

                            <Typography variant="h6" color="primary.main" gutterBottom>
                                Loading Route Details
                            </Typography>

                            <Typography variant="body1" color="text.secondary" paragraph>
                                Fetching complete information from the server...
                            </Typography>

                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    bgcolor: blue[50],
                                    color: blue[700],
                                    px: 2,
                                    py: 1,
                                    borderRadius: 2,
                                    mt: 2
                                }}
                            >
                                <Icon path={mdiCloudDownloadOutline} size={0.8} />
                                <Typography variant="body2">
                                    Retrieving segments, locations and notifications
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1
                            }}>
                                {/* Route Summary */}
                                <Box sx={{
                                    p: 3,
                                    bgcolor: grey[50],
                                    flexShrink: 0
                                }}>
                                <Box sx={{
                                    display: 'flex',
                                    gap: 2,
                                    mt: 2,
                                    position: 'relative'
                                }}>
                                    {/* Main content area */}
                                    <Box sx={{
                                        flex: 1,
                                        position: 'relative',
                                            pr: 4
                                    }}>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 2,
                                                ml: -1
                                        }}>
                                        <Icon path={mdiMapMarkerPath} size={1} color={blue[700]} />
                                            <Box sx={{ flex: 1, width: '100%' }}>
                                            <RouteLocationsView route={route} />
                                        </Box>
                                    </Box>
                                </Box>

                                    {/* Route Stats - Moved to right side with vertical layout */}
                                    <Box sx={{
                                        width: '140px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        bgcolor: 'rgba(245, 245, 245, 0.5)',
                                        p: 2,
                                        borderRadius: 2,
                                        border: `1px solid ${grey[200]}`,
                                        alignSelf: 'stretch',
                                        justifyContent: 'center',
                                        minHeight: '150px'
                                    }}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Icon path={mdiRuler} size={1} color={orange[700]} />
                                            <Typography variant="h6" sx={{ mt: 0.5, color: orange[700], fontWeight: 600 }}>
                                                {((route?.routeLength || 0)).toFixed(1)} km
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Distance
                                            </Typography>
                                        </Box>

                                        <Divider sx={{ my: 1 }} />

                                        <Box sx={{ textAlign: 'center' }}>
                                            <Icon path={mdiClockOutline} size={1} color={green[700]} />
                                            <Typography variant="h6" sx={{ mt: 0.5, color: green[700], fontWeight: 600 }}>
                                                {formatTime(route?.routeDuration || 0)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Duration
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>

                                <Divider sx={{ flexShrink: 0 }} />

                                {/* Tabs - Stay fixed below summary */}
                                <Box sx={{
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    flexShrink: 0
                                }}>
                                <Tabs value={activeTab} onChange={handleTabChange}>
                                    <Tab label="Segments" icon={<Icon path={mdiRoadVariant} size={0.8} />} iconPosition="start" />
                                    <Tab label="Rules" icon={<Icon path={mdiTruckDelivery} size={0.8} />} iconPosition="start" />
                                    <Tab label="Notifications" icon={<Icon path={mdiEmailOutline} size={0.8} />} iconPosition="start" />
                                    <Tab label="Revisions" icon={<Icon path={mdiHistory} size={0.8} />} iconPosition="start" />
                                </Tabs>
                            </Box>

                                {/* Tab Panels - Scrollable content */}
                            <Box sx={{
                                flex: 1,
                                    overflowY: 'auto',
                                    p: 2
                            }}>
                                {/* Segments Panel */}
                                {activeTab === 0 && (
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>#</TableCell>
                                                    <TableCell>Name</TableCell>
                                                    <TableCell>Distance</TableCell>
                                                    <TableCell>Load Type</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(route?.segments || []).map((segment, index) => (
                                                    <TableRow key={index} hover>
                                                        <TableCell>{index + 1}</TableCell>
                                                        <TableCell>{segment.name}</TableCell>
                                                        <TableCell>{(segment.segmentLength).toFixed(1)} km</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={segment.loadType === 'loaded' ? 'Loaded' : 'Empty'}
                                                                size="small"
                                                                color={segment.loadType === 'loaded' ? 'success' : 'default'}
                                                                variant={segment.loadType === 'loaded' ? 'filled' : 'outlined'}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}

                                {/* Rules Panel */}
                                {activeTab === 1 && (
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2 }}>
                                        {route.rules && Object.entries(route.rules).map(([key, value]) => (
                                            <Paper
                                                key={key}
                                                elevation={0}
                                                variant="outlined"
                                                sx={{ p: 2, borderRadius: 2 }}
                                            >
                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                                </Typography>
                                                <Typography variant="h6" color="primary">
                                                    {formatRuleValue(key, value)}
                                                </Typography>
                                            </Paper>
                                        ))}
                                    </Box>
                                )}

                                {/* Notifications Panel - Redesigned for compactness */}
                                {activeTab === 2 && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {route.notifications && Object.entries(route.notifications).map(([type, recipients]) => (
                                            recipients.length > 0 && (
                                                <Box key={type} sx={{ mb: 2 }}>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        mb: 1,
                                                        pb: 1,
                                                        borderBottom: 1,
                                                        borderColor: 'divider'
                                                    }}>
                                                        <Icon
                                                            path={type === 'email' ? mdiEmailOutline : mdiPhoneOutline}
                                                            size={0.8}
                                                            color={type === 'email' ? blue[700] : green[700]}
                                                        />
                                                        <Typography variant="subtitle1" fontWeight={500} sx={{ color: type === 'email' ? blue[700] : green[700] }}>
                                                            {type.charAt(0).toUpperCase() + type.slice(1)} Notifications ({recipients.length})
                                                        </Typography>
                                                    </Box>

                                                <TableContainer sx={{ maxHeight: 400 }}>
                                                    <Table size="small" stickyHeader>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell sx={{ width: '30%', fontWeight: 500 }}>Recipient</TableCell>
                                                                <TableCell sx={{ width: '30%', fontWeight: 500 }}>Contact</TableCell>
                                                                <TableCell sx={{ fontWeight: 500 }}>Notification Types</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {recipients.map((recipient, index) => (
                                                                <TableRow
                                                                    key={index}
                                                                    hover
                                                                    sx={{
                                                                        '&:nth-of-type(odd)': {
                                                                            bgcolor: grey[50]
                                                                        }
                                                                    }}
                                                                >
                                                                    <TableCell>
                                                                        <Typography variant="body2" fontWeight={500}>
                                                                            {recipient.name || '(No name)'}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            {recipient[type === 'email' ? 'email' : 'number']}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Box sx={{
                                                                            display: 'flex',
                                                                            flexWrap: 'wrap',
                                                                            gap: 0.5
                                                                        }}>
                                                                            {recipient.categories.includes('activeStatus') ? (
                                                                                <Chip
                                                                                    label="All Notifications"
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                    color="primary"
                                                                                    sx={{ height: 22, '& .MuiChip-label': { px: 1, fontSize: '0.75rem' } }}
                                                                                />
                                                                            ) : (
                                                                                recipient.categories.map((category, idx) => (
                                                                                    <Chip
                                                                                        key={idx}
                                                                                        label={formatCategory(category)}
                                                                                        size="small"
                                                                                        variant="outlined"
                                                                                        sx={{
                                                                                            height: 22,
                                                                                            '& .MuiChip-label': {
                                                                                                px: 1,
                                                                                                fontSize: '0.75rem'
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                ))
                                                                            )}
                                                                        </Box>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Box>
                                        )
                                    ))}

                                        {/* Empty state if no notifications */}
                                        {(!route.notifications || Object.values(route.notifications).every(recipients => recipients.length === 0)) && (
                                            <Box sx={{
                                                p: 3,
                                                textAlign: 'center',
                                                color: 'text.secondary',
                                                bgcolor: grey[50],
                                                borderRadius: 1
                                            }}>
                                                <Icon path={mdiEmailOutline} size={1.2} color={grey[400]} />
                                                <Typography variant="body1" sx={{ mt: 1 }}>
                                                    No notifications configured for this route
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                {/* Revisions Panel */}
                                {activeTab === 3 && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {revisionEntries.length === 0 && (
                                            <Box sx={{
                                                p: 3,
                                                textAlign: 'center',
                                                color: 'text.secondary',
                                                bgcolor: grey[50],
                                                borderRadius: 1
                                            }}>
                                                <Icon path={mdiHistory} size={1.2} color={grey[400]} />
                                                <Typography variant="body1" sx={{ mt: 1 }}>
                                                    No revisions recorded for this route
                                                </Typography>
                                            </Box>
                                        )}

                                        {revisionEntries.map((entry, index) => (
                                            <Paper
                                                key={`${entry.archivedAt || index}-${entry.tripName || 'rev'}`}
                                                variant="outlined"
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    borderLeft: '4px solid #6A5ACD'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                                                    <Box>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                            {entry?.name || `Revision ${index + 1}`}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                            {entry?.reason || 'No reason provided'}
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        label={`Archived ${formatDateOnly(entry?.archivedAt)}`}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: 'rgba(106,90,205,0.1)',
                                                            color: '#6A5ACD',
                                                            fontWeight: 600,
                                                            '& .MuiChip-label': { px: 1 }
                                                        }}
                                                    />
                                                </Box>

                                                <Divider sx={{ my: 1.5 }} />

                                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1.5 }}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">Trip</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {entry?.tripName || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">Truck</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {entry?.truckRegistrationNumber || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">Trip Period</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {formatTripPeriod(entry)}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">Route Name</Typography>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {entry?.routeName || route?.routeName || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Paper>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>
            </Paper>
        </Modal>
    );
};

export default RouteDetailView;
